const BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api');

function getToken() {
  return localStorage.getItem('token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    // Don't redirect on auth endpoints (login/register) — just throw the error
    if (path.startsWith('/auth/')) {
      const error = await res.json().catch(() => ({ message: 'Invalid credentials' }));
      throw new Error(error.error || error.message || 'Invalid credentials');
    }
    // Token expired — clear and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('activeClient');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Session expired. Please sign in again.');
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    // Trial ended and no subscription — send them to the payment page
    if (res.status === 403 && error.code === 'SUBSCRIPTION_REQUIRED') {
      if (window.location.pathname !== '/subscribe') {
        window.location.href = '/subscribe';
      }
    }
    throw new Error(error.error || error.message || `Request failed: ${res.status}`);
  }

  if (res.status === 204) return null;
  return res.json();
}

// Auth
export function login(email, password) {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function register(name, email, password) {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  });
}

export function getMe() {
  return request('/auth/me');
}

export function changePassword(currentPassword, newPassword) {
  return request('/auth/change-password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function forgotPassword(email) {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function resetPassword(token, password) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export function signup(name, email, password, turnstileToken) {
  return request('/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ name, email, password, turnstileToken }),
  });
}

export function verifyEmail(token) {
  return request(`/auth/verify-email/${token}`);
}

// Clients
export function getClients() {
  return request('/clients');
}

export function createClient(name) {
  return request('/clients', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function deleteClient(id) {
  return request(`/clients/${id}`, { method: 'DELETE' });
}

// Maps
export function getMaps(clientFilter) {
  const qs = clientFilter ? `?clientId=${clientFilter}` : '';
  return request(`/maps${qs}`);
}

export function createMap(data) {
  return request('/maps', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getMap(id) {
  return request(`/maps/${id}`);
}

export function updateMap(id, data) {
  return request(`/maps/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteMap(id) {
  return request(`/maps/${id}`, { method: 'DELETE' });
}

// Tiles
export function createTile(mapId, data) {
  return request(`/maps/${mapId}/tiles`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateTile(mapId, tileId, data) {
  return request(`/maps/${mapId}/tiles/${tileId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteTile(mapId, tileId) {
  return request(`/maps/${mapId}/tiles/${tileId}`, { method: 'DELETE' });
}

export function reorderTiles(mapId, tiles) {
  return request(`/maps/${mapId}/tiles/reorder`, {
    method: 'PUT',
    body: JSON.stringify(tiles),
  });
}

// Versions
export function getVersions(mapId) {
  return request(`/maps/${mapId}/versions`);
}

export function saveVersion(mapId) {
  return request(`/maps/${mapId}/versions`, { method: 'POST' });
}

export function restoreVersion(mapId, versionId) {
  return request(`/maps/${mapId}/versions/${versionId}/restore`, {
    method: 'POST',
  });
}

// Share
export function getShareToken(mapId) {
  return request(`/maps/${mapId}/share`, { method: 'POST' });
}

export function revokeShareToken(mapId) {
  return request(`/maps/${mapId}/share`, { method: 'DELETE' });
}

export async function getSharedMap(token) {
  const res = await fetch(`${BASE_URL}/share/${token}`);
  if (!res.ok) throw new Error('Map not found');
  return res.json();
}

// Admin
export function getAdminStats() {
  return request('/admin/stats');
}

export function getUsers() {
  return request('/admin/users');
}

export function createUser(data) {
  return request('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateUser(userId, data) {
  return request(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function resetUserPassword(userId, password) {
  return request(`/admin/users/${userId}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
}

export function deleteUser(userId) {
  return request(`/admin/users/${userId}`, { method: 'DELETE' });
}

export function manageUserTrial(userId, action, days, clientId) {
  return request(`/admin/users/${userId}/trial`, {
    method: 'PUT',
    body: JSON.stringify({ action, days, clientId }),
  });
}

// Billing
export function createCheckout() {
  return request('/billing/checkout', { method: 'POST' });
}

export function createPortalSession() {
  return request('/billing/portal', { method: 'POST' });
}

export function getBillingStatus() {
  return request('/billing/status');
}

// Webinar
export function getWebinarStatus() {
  return request('/webinar/status');
}

export function registerForWebinar(name, email, problem) {
  return request('/webinar/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, problem }),
  });
}

export function getWebinarAttendees() {
  return request('/webinar/attendees');
}

export function deleteWebinarAttendee(id) {
  return request(`/webinar/attendees/${id}`, { method: 'DELETE' });
}

// Maintenance mode (admin)
export function getMaintenance() {
  return request('/admin/maintenance');
}

export function setMaintenance(on) {
  return request('/admin/maintenance', {
    method: 'POST',
    body: JSON.stringify({ on }),
  });
}

export function importUsersCSV(rows) {
  return request('/admin/import-csv', {
    method: 'POST',
    body: JSON.stringify(rows),
  });
}
